import {Component, Children, cloneElement} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uuidV4 from 'uuid/v4';

export default class RadioGroup extends Component {
    static propTypes = {
        /** Additional class(es) to add to the component. */
        className: PropTypes.string,
        /** Children should only be made up of Radio components. */
        children: PropTypes.arrayOf(PropTypes.element).isRequired,
        /** A unique name which is used to identify this element. If belonging to a form this is used as the key for validation bindings and is required. The name will be passed down to child Radio components to set the proper value upon selection. */
        name: PropTypes.string.isRequired,
        /** Whether to disable all the Radio items in this group. */
        disabled: PropTypes.bool,
        /** A custom label (typically a Label component) to display next to the radio. */
        label: PropTypes.string.isRequired,
        /** Whether this field is required during validation. */
        required: PropTypes.bool,
        /** A custom validation error (typically a FormElementError component) to display when this component is part of a form and is invalid. */
        error: PropTypes.element,
        /**
         * Function to execute when the selected state changes.
         *
         * @param {SyntheticEvent} evt - The React `SyntheticEvent`.
         */
        onChange: PropTypes.func,
        /**
         * Function to execute when the element is marked as invalid by a validation check.
         *
         * @param {ValidityState} validationState - ValidityState object for more fine grained control over error handling in a parent component if desired.
         */
        onInvalid: PropTypes.func,
    };
    state = {
        validationState: null,
    };

    constructor(props) {
        super(props);

        this._ID = `RadioGroup-${uuidV4()}`;
    }

    _onValidationChange = validationState => {
        const {onInvalid} = this.props;

        this.setState({
            validationState,
        });

        if (onInvalid) {
            onInvalid(validationState);
        }
    };

    render() {
        const {
            className,
            children,
            name,
            label,
            required,
            disabled,
            error,
            onChange,
        } = this.props;
        const {validationState} = this.state;
        const isValid = validationState ? validationState.valid : true;
        const classes = classNames(
            'RadioGroup',
            'FormElement',
            {'FormElement-contains-error': !isValid},
            className
        );

        return (
            <div className={classes} id={this._ID}>
                <fieldset className="FormElement-control-container">
                    <legend>
                        <h1 className="heading-md" htmlFor={this._ID}>
                            {label}
                        </h1>
                    </legend>
                    {Children.map(children, child => {
                        return cloneElement(child, {
                            name,
                            disabled,
                            required,
                            onChange,
                            id: this._ID,
                            onValidationChange: this._onValidationChange,
                        });
                    })}
                </fieldset>
                {!isValid && error}
            </div>
        );
    }
}
